import Grid from '@mui/material/Grid2';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import { useState } from 'react';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import Collapse from '@mui/material/Collapse';
import Typography from '@mui/material/Typography';
import { TCustomTheme } from '@mgm/common/theme';
import { useCustomTheme } from '@mgm/common/hooks/useCustomTheme';
import { Iconify } from '@mgm/common/components';
import { BaseLabel } from '@mgm/common/components/base';
import { useAppDispatch, useAppSelector } from '../../../../hooks/useAppStore';
import { getCurrencyByCode } from '../../../../store/common/selectors';
import { getSelectedCommissionPlan } from '../../../../store/commissions/selectors';
import { commissionsActions } from '../../../../store/commissions/slice';
import { StepInput } from './StepInput';
import { TCommissionPlan } from '../../../../types';

const CreateStepsInput = () => {
  const theme = useTheme() as TCustomTheme;
  const { isMobile } = useCustomTheme(theme);
  const getCurrency = useAppSelector(getCurrencyByCode);
  const selectedCommissionPlan = useAppSelector(getSelectedCommissionPlan);
  const dispatch = useAppDispatch();
  const { t } = useTranslation('commissions');
  const [readMore, setReadMore] = useState(false);

  const handleStepsChange = (updatedSteps: TCommissionPlan['commissionSteps']) => {
    dispatch(commissionsActions.setSelectedCommissionsPlan({
      ...selectedCommissionPlan,
      commissionSteps: updatedSteps,
    }));
  };

  const handleChange = () => {
    setReadMore((prev) => !prev);
  };

  return (
    <Grid
      {...(isMobile && {
        paddingX: 0,
      })}
      paddingTop={0}
      size={{ sm: 12, md: 12, lg: 12 }}
    >
      <Divider sx={{
        mt: 3,
        mb: 2,
        borderStyle: 'dashed',
      }}
      />
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
        <BaseLabel letterSpacing="-0.5px">{t('commissionSteps')}</BaseLabel>
        <Box py={1}>
          <Button endIcon={<Iconify icon="mdi:help-outline" />} onClick={handleChange} variant="outlined">{t('example')}</Button>
        </Box>
      </Stack>
      <Collapse orientation="vertical" in={readMore}>
        <Divider sx={{
          my: 2,
          borderStyle: 'dashed',
        }}
        />
        <Stack spacing={1}>
          <Typography fontWeight={600} variant="caption">
            {t('letsTakeALookAtTheFollowingSteps')}
          </Typography>
          <Typography color="primary" fontWeight={600} variant="caption">
            {`${t('step')} 1`}
          </Typography>
          <Typography variant="caption">
            {`${t('from')} 1$ ${t('to')} 1K$ -> 10% (${t('percentage')})`}
          </Typography>
          <Typography color="primary" fontWeight={600} variant="caption">
            {`${t('step')} 2`}
          </Typography>
          <Typography variant="caption">
            {`${t('from')} 1K$ ${t('to')} 5K$ -> 15% (${t('percentage')})`}
          </Typography>
          <Typography color="primary" fontWeight={600} variant="caption">
            {`${t('step')} 3`}
          </Typography>
          <Typography variant="caption">
            {`${t('from')} 5K$ ${t('to')} 25K$ -> 800$ (${t('flat')})`}
          </Typography>
          <Typography variant="caption" fontWeight={600}>
            {t('thisTranslatesTo')}
          </Typography>
          <Typography variant="caption">
            {t('theEmployeeSoldForHeHasReached')}
          </Typography>
          <Typography variant="caption">
            {t('theEmployeeSoldForHeHasReachedTheSecond')}
          </Typography>
          <Typography variant="caption">
            {t('theEmployeeSoldForHeHasReachedTheThird')}
          </Typography>
        </Stack>
        <Divider sx={{
          my: 2,
          borderStyle: 'dashed',
        }}
        />
      </Collapse>
      <Box pt={2}>
        <StepInput
          steps={selectedCommissionPlan.commissionSteps}
          onChange={handleStepsChange}
          maxSteps={5}
          currencyMark={getCurrency().mark}
        />
      </Box>
    </Grid>
  );
};

export { CreateStepsInput };
